import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';

import ErrorPage from 'containers/ErrorPage';

import {
  ENTITY_MAIN_TABS,
  ENTITY_DETAILS_TAB,
  ENTITY_TYPE,
  URL_STATIC_PATHS,
  RESPONSE_STATUS_CODES,
} from 'constants/index';

import getServerProps from 'utils/globals/ssrQueries';
import usePageContext from 'utils/hooks/usePageContext';
import withAuthCheck from 'utils/hocs/withAuthCheck';

import { getPageTranslations } from 'src/i18n';
import orgReducer, { key as orgKey } from 'containers/Organizations/reducer';
import editImagesReducer, { key as editImagesKey } from 'containers/EditImages/reducer';
import { useInjectReducer } from 'store/injectReducer';

const Home = dynamic(() => import('containers/Home'));
const StubPageWrapper = dynamic(() => import('components/Globals/Layout/StubPage/Wrapper'));
const AboutOrg = dynamic(() => import('components/Organization/Display/AboutOrg'));
const EntityContactBlock = dynamic(() => import('components/Globals/EntityContactBlock'));
const Venues = dynamic(() => import('components/Organization/Display/Venues'));
const EntityRentalsList = dynamic(() => import('components/Productions/Display/EntityRentalsList'));
const EntityCalendarVenues = dynamic(() => import('components/Productions/Display/EntityCalendarVenues'));
const Seasons = dynamic(() => import('components/Productions/Display/Seasons'));

const OrganizationHome = dynamic(() => import('components/Organization/Display/Home'));
const Reviews = dynamic(() => import('components/Globals/Layout/StubPage/Tabs/Reviews'));
const ReviewsPage = dynamic(() => import('components/Globals/Layout/StubPage/Tabs/ReviewsPage'));
const Videos = dynamic(() => import('components/Globals/Layout/StubPage/Tabs/Videos'));
const VideoGallery = dynamic(() => import('components/Globals/Layout/StubPage/Tabs/VideoGallery'));
const DigitalVideoPage = dynamic(() => import('components/Globals/Layout/StubPage/Tabs/DigitalVideoPage'));
const Photos = dynamic(() => import('components/Globals/Layout/StubPage/Tabs/Photos'));
const PhotoGallery = dynamic(() => import('components/Globals/Layout/StubPage/Tabs/PhotoGallery'));

const OrganizationEdit = dynamic(() => import('components/Organization/Edit'));
const ModifyProduction = dynamic(() => import('components/Productions/Edit/ModifyProduction'));
const SearchProductions = dynamic(() => import('components/Productions/Edit/SearchProductions'));
const ReviveProduction = dynamic(() => import('components/Productions/Edit/ReviveProduction'));
const ValidateProduction = dynamic(() => import('components/Productions/Edit/ValidateProduction'));

const EditPages = withAuthCheck(() => {
  useInjectReducer([
    { key: orgKey, reducer: orgReducer },
    { key: editImagesKey, reducer: editImagesReducer },
  ]);
  const { entityType, mainPath } = usePageContext();

  if (mainPath) {
    switch (mainPath) {
      case URL_STATIC_PATHS.MODIFY_PRODUCTION: {
        return <ModifyProduction />;
      }
      case URL_STATIC_PATHS.REVIVALS: {
        return <ReviveProduction />;
      }
      case URL_STATIC_PATHS.SEARCH_PRODUCTIONS: {
        return <SearchProductions />;
      }
      case URL_STATIC_PATHS.VALIDATION: {
        if (entityType === ENTITY_TYPE.ARTIST) {
          return <ValidateProduction />;
        }

        return <ErrorPage statusCode={403} />;
      }
      default: {
        return <ErrorPage statusCode={403} />;
      }
    }
  }

  if (entityType === ENTITY_TYPE.ORGANIZATION) {
    return <OrganizationEdit />;
  }

  return <ErrorPage statusCode={403} />;
});

const EntityStubPages = ({ statusCode }) => {
  const { entityType, mainPath, subPath, entity, isLoading } = usePageContext();

  const TabComponent = useMemo(() => {
    if (statusCode !== RESPONSE_STATUS_CODES.SUCCESS) {
      return null;
    }

    switch (mainPath) {
      case ENTITY_MAIN_TABS.VIDEOS:
        switch (subPath) {
          case ENTITY_DETAILS_TAB.DIGITAL:
            return DigitalVideoPage;
          case ENTITY_DETAILS_TAB.GENERAL:
            return VideoGallery;
          default: {
            return Videos;
          }
        }
      case ENTITY_MAIN_TABS.IMAGES:
        switch (subPath) {
          case ENTITY_DETAILS_TAB.GENERAL:
            return PhotoGallery;
          default: {
            return Photos;
          }
        }
      case ENTITY_MAIN_TABS.REVIEWS: {
        switch (subPath) {
          case ENTITY_DETAILS_TAB.GENERAL:
            return ReviewsPage;
          default: {
            return Reviews;
          }
        }
      }
      case ENTITY_MAIN_TABS.VENUES: {
        return Venues;
      }
      case ENTITY_MAIN_TABS.SEASONS:
        return Seasons;
      case ENTITY_MAIN_TABS.CALENDAR:
        return EntityCalendarVenues;
      case ENTITY_MAIN_TABS.ABOUT: {
        return AboutOrg;
      }
      case ENTITY_MAIN_TABS.CONTACT:
        return EntityContactBlock;
      case ENTITY_MAIN_TABS.RENTALS:
        return EntityRentalsList;
      case ENTITY_MAIN_TABS.HOME:
      default:
        if (!mainPath) {
          switch (entityType) {
            case ENTITY_TYPE.ORGANIZATION:
              return OrganizationHome;
            default:
              return null;
          }
        }

        return null;
    }
  }, [statusCode, mainPath, subPath, entityType]);

  if (!TabComponent) {
    return <ErrorPage statusCode={statusCode} />;
  }

  return (
    <StubPageWrapper>
      <TabComponent
        entityType={entityType}
        entity={entity}
        isLoading={isLoading}
        mainPath={mainPath}
        subPath={subPath}
      />
    </StubPageWrapper>
  );
};

const RootPage = ({ statusCode }) => {
  const { entityType, edit, paths } = usePageContext();

  if (entityType) {
    if (edit) {
      return <EditPages />;
    }

    return <EntityStubPages statusCode={statusCode} />;
  }

  if (paths?.length > 0) {
    return <ErrorPage statusCode={404} />;
  }

  return <Home />;
};

RootPage.i18nNamespaces = getPageTranslations(['NS_ENTITY_STUB_PAGE']);

export async function getServerSideProps(ctx) {
  const { redirect, context, ...serverProps } = await getServerProps(ctx);

  if (redirect?.destination) {
    ctx.res.writeHead(redirect?.statusCode, { location: redirect?.destination });
    ctx.res.end();
  }

  return serverProps;
}

export default RootPage;
